import { DeleteOutlined, FormOutlined, PlusOutlined, ScheduleOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Checkbox, DatePicker, Form, Input, InputNumber, Modal, Select, Table, TreeSelect } from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import adGroupApi from 'src/component/app/adGroup/AdGroupApi'
import * as routes from 'src/constants/routes'
import { useUser } from 'src/context/UserContext'
import { toSort } from 'src/util/useSorter'
import { AdGroupBean, CampaignBean, CreativeBean, FilterListBean, LanguageBean } from '../../../api/api'
import { formDataLoader, FormDataType } from '../../../util/FormDataLoader'
import { capitalizedView, getIabCategoriesAsTree, isAdmin } from '../../../util/Util'
import AnchorWrapper from '../anchor/AnchorWrapper'
import creativeApi from '../creative/CreativeApi'
import geoApi from '../geo/GeoApi'
import GeoTargeting, { GeoItem, toBean } from '../geo/GeoTargeting'
import { fromBean, MapPoint, MapSelector } from '../map/MapSelector'
import { WithLoadedMap } from '../map/WithLoadedMap'
import { ALWAYS_WORK, clear, renderDay, Scheduler } from '../schedule/Scheduler'
import { anchorItems, getGeoPrecision } from './AdGroupCreate'
import './style.css'

const FormItem = Form.Item
const Option = Select.Option
const Column = Table.Column

const AdGroupEdit = () => {

    const formInfoItems: FormDataType[] = [
        FormDataType.Languages,
        FormDataType.Campaigns,
        FormDataType.IabCategories
    ]

    const { user } = useUser()

    const [info, setInfo] = useState({
        languages: [],
        campaigns: [],
        iabCategories: [],
        filterLists: []
    })

    const [creatives, setCreatives] = useState([])

    const navigateTo = useNavigate()
    const { id } = useParams<{ id: string }>()
    const [error, setError] = useState(undefined)
    const [form] = Form.useForm()
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')

    const [geoItems, setGeoItems] = useState<GeoItem[]>([])

    const [schedule, setSchedule] = useState<number[][]>([])
    const [scheduleVisible, setScheduleVisible] = useState(false)

    const [mapPoint, setMapPoint] = useState<MapPoint>({})
    const [mapVisible, setMapVisible] = useState(false)

    const handleSubmit = async () => {
        const values = await form.validateFields()
        try {
            await adGroupApi.edit(id, { ...values, startDate, endDate, geoItems: geoItems.map(toBean), ...mapPoint, schedule: clear(schedule) })
            navigateTo('/ad-group')
        } catch (e) {
            setError(e.error)
        }
    }

    const admin = isAdmin(user)

    const loadCreatives = async () => {
        const creativeData: any = await creativeApi.allByAdGroup(id, toSort({ sort: 'id', direction: 'asc' }))
        const list = creativeData.data.map(item => item.data as CreativeBean)
        setCreatives(list)
    }

    const loadData = async () => {
        try {
            const response: any = await adGroupApi.view(id)
            const bean = response.data

            if (bean.startDate) {
                setStartDate(bean.startDate)
                bean.startDate = dayjs(bean.startDate, 'YYYY-MM-DD HH:mm')
            }
            if (bean.endDate) {
                setEndDate(bean.endDate)
                bean.endDate = dayjs(bean.endDate, 'YYYY-MM-DD HH:mm')
            }
            form.setFieldsValue(bean)

            setGeoItems(bean.geoItems)
            setSchedule(bean.schedule || ALWAYS_WORK)
            setMapPoint(fromBean(bean))

            if (admin) formInfoItems.push(FormDataType.FilterLists)

            const formInfo = await formDataLoader(...formInfoItems)
            setInfo(formInfo as any)

            loadCreatives()
        } catch (e) {
            setError(e.error)
        }
    }

    useEffect(() => {
        loadData()
    }, [id])

    const removeCreative = (record: CreativeBean) => {
        Modal.confirm({
            title: `Delete ${record.name}?`,
            content: 'Are you sure?',
            cancelText: 'NO',
            okText: 'Yes, I\'m sure',
            onOk: async () => {
                await creativeApi.delete(record.id as number)
                loadCreatives()
            }
        })
    }

    return <div className="anchored-form">
        <Form
            style={{
                width: '1000px'
            }}
            onFinish={handleSubmit}
            form={form}
            labelCol={{ span: 5 }}
            wrapperCol={{ span: 16 }}
        >
            {error ? <Alert message={error} type="error" /> : null}
            <Card title="General" size="small" className="form-card" id="general">
                <FormItem
                    required
                    name="name"
                    label="Name"
                >
                    <Input placeholder="Name" />
                </FormItem>
                <FormItem
                    name="status"
                    label="Status"
                    required
                >
                    <Select placeholder="Select Status">
                        {Object.values(AdGroupBean.StatusEnum).map(type => <Option key={type} value={type}>{capitalizedView(type)}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="campaignId"
                    required
                    label="Campaign"
                >
                    <Select
                        showSearch
                        optionFilterProp="title"
                        placeholder="Select Campaign"
                    >
                        {info.campaigns.map((item: CampaignBean) => <Option title={item.name} key={item.id} value={item.id}>{item.name}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    required
                    name="domain"
                    label="Domain"
                >
                    <Input placeholder="Domain" />
                </FormItem>
                <FormItem
                    name="iabCategoryIds"
                    label="IAB Categories"
                >
                    <TreeSelect
                        allowClear
                        treeCheckable
                        showCheckedStrategy="SHOW_ALL"
                        placeholder="Select IAB categories"
                        treeData={getIabCategoriesAsTree(info.iabCategories)}
                    />
                </FormItem>
                <FormItem
                    required
                    label="Dates"
                >
                    <FormItem
                        name="startDate"
                        className="ant-item ant-item-first form-date-item"
                    >
                        <DatePicker showTime={{ defaultValue: dayjs('00:00', 'HH:mm') }} format="YYYY-MM-DD HH:mm" onChange={(moment, value) => setStartDate(value)} placeholder="Start date" />
                    </FormItem>
                    <FormItem
                        name="endDate"
                        className="ant-item ant-item-second form-date-item"
                    >
                        <DatePicker showTime={{ defaultValue: dayjs('00:00', 'HH:mm') }} format="YYYY-MM-DD HH:mm" onChange={(moment, value) => setEndDate(value)} placeholder="End date" />
                    </FormItem>
                </FormItem>
            </Card>
            <Card title="Budget" size="small" className="form-card" id="budget">
                <FormItem
                    required
                    name="totalBudget"
                    label="Total Budget"
                    className="form-date-item"
                >
                    <InputNumber placeholder="Total Budget" suffix="$" />
                </FormItem>
                <FormItem
                    name="dailyBudget"
                    label="Daily Budget"
                    className="form-date-item"
                >
                    <InputNumber placeholder="Daily Budget" suffix="$" />
                </FormItem>
                <FormItem
                    required
                    name="distribution"
                    label="Distribution"
                >
                    <Select placeholder="Select Distribution Type">
                        {Object.values(AdGroupBean.DistributionEnum).map(type => <Option key={type} value={type}>{type}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    required
                    label="Pricing Type"
                >
                    <FormItem
                        name="biddingStrategy"
                        className="ant-item ant-item-first form-date-item"
                    >
                        <Select allowClear placeholder="Select Pricing Type">
                            {Object.values([AdGroupBean.BiddingStrategyEnum.CPM]).map(type => <Option key={type} value={type}>{type}</Option>)}
                        </Select>
                    </FormItem>
                    <FormItem
                        name="price"
                        className="ant-item ant-item-second form-date-item"
                    >
                        <InputNumber placeholder="Price" suffix="$" />
                    </FormItem>
                </FormItem>
                <FormItem
                    label="Frequency Capping"
                >
                    <FormItem
                        name="fcPeriod"
                        className="ant-item ant-item-first form-date-item"
                    >
                        <Select allowClear placeholder="Select FC period">
                            {Object.keys(AdGroupBean.FcPeriodEnum).map(type => <Option key={type} value={type}>{type}</Option>)}
                        </Select>
                    </FormItem>
                    <FormItem
                        name="fcLimit"
                        className="ant-item ant-item-second form-date-item"
                    >
                        <InputNumber placeholder="FC limit" />
                    </FormItem>
                </FormItem>
            </Card>
            <Card title="Creatives" size="small" className="form-card" id="creatives">
                <FormItem label="Linked Creatives">
                    <Button htmlType="button" type="primary" ghost style={{ width: '23.8%', borderRadius: '8px', margin: '0 1% 0 0' }} icon={<PlusOutlined />} onClick={() => navigateTo(`/creative/create?adGroupId=${id}`)}>
                        New Creative
                    </Button>
                    <Button htmlType="button" type="primary" ghost style={{ width: '23.8%', borderRadius: '8px' }} icon={<PlusOutlined />} onClick={() => navigateTo(`/creative/bulk?adGroupId=${id}`)}>
                        New Bulk
                    </Button>
                </FormItem>
                <FormItem wrapperCol={{ offset: 5, span: 16 }}>
                    <Table
                        rowKey="id"
                        size="small"
                        dataSource={creatives}
                        pagination={false}
                    >
                        <Column
                            key="id"
                            title="ID"
                            dataIndex="id"
                        />
                        <Column
                            key="name"
                            title="Name"
                            dataIndex="name"
                            render={(text, record: CreativeBean) => <Link to={`/creative/${record.id}/edit?adGroupId=${id}`}>{text}</Link>}
                        />
                        <Column
                            key="format"
                            title="Format"
                            dataIndex="format"
                        />
                        <Column align="center" key="action" title="Actions" render={(text: string, record: CreativeBean) => (
                            <span>
                                <Link to={`/creative/${record.id}/edit?adGroupId=${id}`}>
                                    <Button type="text" size="small">
                                        <FormOutlined className="actions-btn" />
                                    </Button>
                                </Link>
                                <Button type="text" size="small" danger onClick={() => removeCreative(record)}>
                                    <DeleteOutlined />
                                </Button>
                            </span>
                        )} />
                    </Table>
                </FormItem>
            </Card>
            <Card title="Targeting" size="small" className="form-card" id="targeting">
                <FormItem
                    name="languages"
                    label="Languages"
                >
                    <Select
                        showSearch
                        allowClear
                        mode="multiple"
                        optionFilterProp="title"
                        placeholder="Select language"
                    >
                        {info.languages.map((item: LanguageBean) => <Option title={item.name} key={item.id} value={item.id}>{item.name}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="devices"
                    label="Devices"
                >
                    <Select mode="multiple" placeholder="Select device">
                        {Object.keys(AdGroupBean.DevicesEnum).map(item => <Option key={item} value={item}>{item}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="brands"
                    label="Brands"
                >
                    <Select mode="multiple" placeholder="Select brand">
                        {Object.keys(AdGroupBean.BrandsEnum).map(item => <Option key={item} value={item}>{item}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="platforms"
                    label="Platforms"
                >
                    <Select mode="multiple" placeholder="Select platform">
                        {Object.keys(AdGroupBean.PlatformsEnum).map(item => <Option key={item} value={item}>{item}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="browsers"
                    label="Browsers"
                >
                    <Select mode="multiple" placeholder="Select browser">
                        {Object.keys(AdGroupBean.BrowsersEnum).map(item => <Option key={item} value={item}>{item}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="contentIabCategoryIds"
                    label="Content IAB Categories"
                >
                    <TreeSelect
                        allowClear
                        treeCheckable
                        showCheckedStrategy="SHOW_ALL"
                        placeholder="Select content IAB categories"
                        treeData={getIabCategoriesAsTree(info.iabCategories)}
                    />
                </FormItem>
                <FormItem
                    name="dealOnly"
                    label="PMP only"
                    valuePropName="checked"
                >
                    <Checkbox />
                </FormItem>
                <FormItem
                    name="dealIdList"
                    label="Deal ID list"
                >
                    <Input placeholder="Deal ID list" />
                </FormItem>
                <FormItem
                    name="filterListIds"
                    label="Filter Lists"
                >
                    <Select
                        showSearch
                        allowClear
                        mode="multiple"
                        optionFilterProp="title"
                        placeholder="Select Filter List"
                    >
                        {admin && info.filterLists.map((item: FilterListBean) => <Option title={item.name} key={item.id} value={item.id as number}>{item.name}</Option>)}
                    </Select>
                </FormItem>
            </Card>
            <Card title="Day Parting" size="small" className="form-card" id="day-parting">
                <FormItem className="compact-ant-item" label="Monday">{renderDay(schedule[0])}</FormItem>
                <FormItem className="compact-ant-item" label="Tuesday">{renderDay(schedule[1])}</FormItem>
                <FormItem className="compact-ant-item" label="Wednesday">{renderDay(schedule[2])}</FormItem>
                <FormItem className="compact-ant-item" label="Thursday">{renderDay(schedule[3])}</FormItem>
                <FormItem className="compact-ant-item" label="Friday">{renderDay(schedule[4])}</FormItem>
                <FormItem className="compact-ant-item" label="Saturday">{renderDay(schedule[5])}</FormItem>
                <FormItem label="Sunday">{renderDay(schedule[6])}</FormItem>
                <FormItem wrapperCol={{ offset: 5, span: 19 }}>
                    <Button htmlType="button" type="primary" ghost style={{ width: '23.8%', borderRadius: '8px' }} onClick={() => setScheduleVisible(true)} icon={<ScheduleOutlined />}>
                        Set Day Parting
                    </Button>
                </FormItem>
                <Scheduler
                    schedule={schedule}
                    visible={scheduleVisible}
                    setSchedule={setSchedule}
                    setVisible={setScheduleVisible}
                />
            </Card>
            <Card title="Geo" size="small" className="form-card" id="geo">
                <FormItem label="Coordinates" className="compact-ant-item">
                    Latitude: {mapPoint.lat ? mapPoint.lat : <span className="form-item-text">not set</span>}
                </FormItem>
                <FormItem wrapperCol={{ offset: 5, span: 16 }} className="compact-ant-item">
                    Longitude: {mapPoint.lon ? mapPoint.lon : <span className="form-item-text">not set</span>}
                </FormItem>
                <FormItem wrapperCol={{ offset: 5, span: 16 }}>
                    Radius: {mapPoint.radius ? mapPoint.radius : <span className="form-item-text">not set</span>}
                </FormItem>
                <FormItem wrapperCol={{ offset: 5, span: 16 }}>
                    <Button className="geo-btn" htmlType="button" type="primary" ghost style={{ width: '14.5rem', borderRadius: '8px' }} onClick={() => setMapVisible(true)} icon={<img src="/resources/images/map_point.svg" alt="" />}>
                        Set Geotargeting
                    </Button>
                </FormItem>
                <WithLoadedMap>
                    <MapSelector
                        point={mapPoint}
                        setPoint={setMapPoint}
                        mapVisible={mapVisible}
                        setMapVisible={setMapVisible}
                    />
                </WithLoadedMap>
                <FormItem
                    name="geoPrecision"
                    label="Geo Precision"
                >
                    <Select placeholder="Select Geo Precision">
                        {Object.values(AdGroupBean.GeoPrecisionEnum).map((type: AdGroupBean.GeoPrecisionEnum) => <Option key={type} value={type}>{getGeoPrecision(type)}</Option>)}
                    </Select>
                </FormItem>
                <GeoTargeting
                    id={id}
                    loadGeo={adGroup => geoApi.adGroupGeo(adGroup)}
                    geoItems={geoItems}
                    setGeoItems={setGeoItems}
                />
            </Card>
            <div className="create-cancel-group">
                <Button type="primary" htmlType="submit">Save</Button>
                <Button htmlType="button" danger onClick={() => navigateTo(routes.AD_GROUP)}>Cancel</Button>
            </div>
        </Form>
        <AnchorWrapper items={anchorItems} />
    </div>
}

export default AdGroupEdit


// WEBPACK FOOTER //
// ./src/component/app/adGroup/AdGroupEdit.tsx